import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/PricingProfile';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const PRICING_PROFILE_API_REDUCER_KEY = 'pricing-profiles';
export const PRICING_PROFILE_PATH = 'pricing-profiles';

export const pricingProfileApi = createApi({
    reducerPath: PRICING_PROFILE_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['PricingProfile', 'PricingProfiles'],
    endpoints: (builder) => ({
        getPricingProfileList: builder.query<PaginationResult<Entity.PricingProfile>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ PRICING_PROFILE_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['PricingProfiles'],
        }),
        getPricingProfile: builder.query<Entity.PricingProfile, Entity.PrimaryPricingProfile>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = pricingProfileNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ PRICING_PROFILE_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['PricingProfile'],
        }),
        getPricingProfileMultiple: builder.query<Entity.PricingProfile, Entity.PrimaryPricingProfile[]>({
            query: (ids) => {
                return ({
                    url: `/${ PRICING_PROFILE_PATH }/ids${Entity.PrimaryPricingProfile.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['PricingProfiles'],
        }),
        getPricingProfilePresentation: builder.query<PaginationResult<Entity.PricingProfile>, Entity.PresentationPricingProfileRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ PRICING_PROFILE_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['PricingProfiles'],
        }),
        
        insertPricingProfile: builder.mutation<Entity.PricingProfile, Entity.CreatePricingProfile>({
            query: (toInsert) => {
                return ({
                    url: `/${ PRICING_PROFILE_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    pricingProfileApi.util.upsertQueryData('getPricingProfile', Entity.PrimaryPricingProfile.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['PricingProfiles'],
        }),
        insertMultiplePricingProfile: builder.mutation<Entity.PricingProfile[], Entity.CreatePricingProfile[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ PRICING_PROFILE_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            pricingProfileApi.util.upsertQueryData('getPricingProfile', Entity.PrimaryPricingProfile.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['PricingProfiles'],
        }),
        updatePricingProfile: builder.mutation<Entity.PricingProfile, Entity.UpdateEntityPricingProfile>({
            query: (toUpdate) => {
                return ({
                    url: `/${ PRICING_PROFILE_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['PricingProfiles'],
        }),
        updateMultiplePricingProfile: builder.mutation<Entity.PricingProfile[], Entity.UpdateEntityPricingProfile[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ PRICING_PROFILE_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['PricingProfiles'],
        }),
        deletePricingProfile: builder.mutation<Entity.PricingProfile, Entity.PricingProfile>({
            query: (toDelete) => {
                return ({
                    url: `/${ PRICING_PROFILE_PATH }/${Entity.PrimaryPricingProfile.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['PricingProfiles'],
        }),
        deleteMultiplePricingProfile: builder.mutation<Entity.PricingProfile[], Entity.PrimaryPricingProfile[]>({
            query: (ids) => {
                return ({
                    url: `/${ PRICING_PROFILE_PATH }/batch${Entity.PrimaryPricingProfile.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['PricingProfiles'],
        }),
    }),
})

export const [pricingProfileNormalizer, pricingProfileNormalizerMiddleware] = createNormalizationMiddleware(PRICING_PROFILE_API_REDUCER_KEY, pricingProfileApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetPricingProfileListQuery,
    useGetPricingProfileMultipleQuery,
    useGetPricingProfilePresentationQuery,
    useGetPricingProfileQuery,
    useInsertPricingProfileMutation,
    useInsertMultiplePricingProfileMutation,
    useUpdatePricingProfileMutation,
    useUpdateMultiplePricingProfileMutation,
    useDeletePricingProfileMutation,
    useDeleteMultiplePricingProfileMutation,
    
} = pricingProfileApi
